import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";
import Badge from "../../ui/badge/Badge";
import { Eye, Webhook, Copy, Check } from 'lucide-react';
import MoreButton from '../../common/MoreButton';
import { useParams } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import Select from "../../../components/form/Select";
import DateRangePicker from "../../../components/daterange/DateRangePicker";
import Modal from '../../common/Modal';
import Alert from '../../ui/alert/Alert';
import { FileText } from 'lucide-react';

// Tipos para o componente Badge
type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark";

// Definição da interface do webhook
interface Webhook {
    id: string;
    webhookId: string;
    companyId: string;
    name: string;
    url: string;
    productList: string[]; // Array de IDs de produtos
    events: string[];
    active: boolean;
    type: string;
    settingsData: unknown;
    createdAt: string;
    updatedAt: string;
    createdById: string | null;
    products?: { // Detalhes dos produtos (pode ser adicionado pela API)
        id: string;
        name: string;
        imageUrl: string | null;
    }[];
    company?: {
        id: string;
        fantasyName: string;
    };
}

interface WebhookDelivery {
    id: string;
    event: string | null;
    success: boolean | null;
    responseStatus: number | null;
    attemptCount: number | null;
    payload: unknown;
    endpoint: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    responseBody: string | null;
}

interface WebhookEventsResponse {
    success: boolean;
    data: {
        webhookId: string;
        name: string;
        deliveries: WebhookDelivery[];
    };
    pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
    };
}

interface WebhooksTableProps {
    webhookId?: string;
}

export default function WebhooksTable({ webhookId }: WebhooksTableProps) {
    const { id: paramId } = useParams<{ id: string }>();
    const webhookIdToUse = webhookId || paramId;

    // Estados para webhooks e loading
    const [webhookDeliveries, setWebhookDeliveries] = useState<WebhookDelivery[]>([]);
    const [loading, setLoading] = useState(true);

    // Estados para filtros
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // Estados para alertas e modais
    const [alertInfo, setAlertInfo] = useState<{ show: boolean; type: 'success' | 'error' | 'info' | 'warning'; title: string; message: string; } | null>(null);
    const [webhookToDelete, setWebhookToDelete] = useState<string | null>(null);
    const [webhookResendModalOpen, setWebhookResendModalOpen] = useState<boolean>(false);
    const [webhookResendLoading, setWebhookResendLoading] = useState<boolean>(false);
    const [deliveryId, setDeliveryId] = useState<string | null>(null);
    const [webhookDetailsModalOpen, setWebhookDetailsModalOpen] = useState<boolean>(false);
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    const handleCopyContent = async (key: string, content: string) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
        } catch (err) {
            console.error("Erro ao copiar conteúdo:", err);
        }
    };

    // Carregar datas do localStorage (se existirem) ou definir padrão
    const getSavedDateRange = () => {
        try {
            const savedRange = localStorage.getItem('dateRangeSelection');
            if (savedRange) {
                const { startDate, endDate } = JSON.parse(savedRange);
                return {
                    startDate: startDate ? new Date(startDate) : null,
                    endDate: endDate ? new Date(endDate) : null
                };
            }
        } catch (error) {
            console.error("Erro ao recuperar datas do localStorage:", error);
        }

        // Se não houver dados salvos ou houver erro, definir últimos 7 dias
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 6);
        sevenDaysAgo.setHours(0, 0, 0, 0);

        return {
            startDate: sevenDaysAgo,
            endDate: today
        };
    };

    // Estado para armazenar as datas selecionadas
    const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(getSavedDateRange());

    // Opções para o select de eventos
    const options = [
        { value: "all", label: "Todos os eventos" },
        { value: "sale_approved", label: "Venda Aprovada" },
        { value: "sale_rejected", label: "Venda Recusada" },
        { value: "sale_refunded", label: "Venda Reembolsada" },
        { value: "sale_chargeback", label: "Chargeback" },
        { value: "sale_abandoned", label: "Abandono" },
        { value: "sale_pix_generated", label: "PIX Gerado" },
        { value: "sale_boleto_generated", label: "Boleto Gerado" },
        { value: "subscription_canceled", label: "Assinatura Cancelada" },
        { value: "subscription_overdue", label: "Assinatura Atrasada" },
        { value: "subscription_renewed", label: "Assinatura Renovada" }
    ];

    // Função para formatação de data
    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        }).format(date);
    };

    // Função para buscar os eventos de webhook
    const fetchWebhookEvents = async () => {
        if (!webhookIdToUse) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = authService.getToken();
            if (!token) {
                console.error('Token não encontrado');
                setLoading(false);
                return;
            }

            // Construir parâmetros de filtro
            const params = new URLSearchParams();
            params.append('page', currentPage.toString());
            params.append('limit', itemsPerPage.toString());

            // Adicionar filtro de status se não for "all"
            if (statusFilter && statusFilter !== "all") {
                params.append('eventType', statusFilter);
            }

            // Adicionar filtro de busca se houver
            if (search) {
                params.append('search', search);
            }

            // Adicionar filtros de data se houver
            if (dateRange.startDate) {
                const startDateStr = formatDateForAPI(dateRange.startDate);
                params.append('startDate', startDateStr);
            }

            if (dateRange.endDate) {
                const endDateStr = formatDateForAPI(dateRange.endDate);
                params.append('endDate', endDateStr);
            }

            const response = await api.get<WebhookEventsResponse>(
                `/api/company-webhook/${webhookIdToUse}/events?${params.toString()}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setWebhookDeliveries(response.data.data.deliveries);

                // Atualizar informações de paginação
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.pages);
                }
            } else {
                setAlertInfo({
                    show: true,
                    type: 'error',
                    title: 'Erro',
                    message: 'Não foi possível carregar os eventos do webhook'
                });
                console.error('Erro ao buscar eventos:', response.data);
            }
        } catch (error) {
            console.error('Erro ao buscar eventos do webhook:', error);
            setAlertInfo({
                show: true,
                type: 'error',
                title: 'Erro',
                message: 'Erro ao buscar eventos do webhook'
            });
        } finally {
            setLoading(false);
        }
    };

    // Formatar data para API
    const formatDateForAPI = (date: Date): string => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Efeito para buscar os eventos quando os filtros mudarem
    useEffect(() => {
        if (webhookIdToUse) {
            fetchWebhookEvents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webhookIdToUse, currentPage, statusFilter, dateRange.startDate, dateRange.endDate, search]);

    // Salvar seleção de datas no localStorage quando mudar
    useEffect(() => {
        try {
            localStorage.setItem('dateRangeSelection', JSON.stringify({
                startDate: dateRange.startDate?.toISOString(),
                endDate: dateRange.endDate?.toISOString()
            }));
        } catch (error) {
            console.error("Erro ao salvar datas no localStorage:", error);
        }
    }, [dateRange]);

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Função para quando o período de datas mudar
    const handleDateRangeChange = (range: { startDate: Date | null; endDate: Date | null }) => {
        setDateRange({
            startDate: range.startDate,
            endDate: range.endDate
        });

        // Resetar para página 1 ao mudar filtros
        setCurrentPage(1);
    };

    // Função para abrir o modal de teste
    const openWebhookModal = (webhookId: string) => {
        setWebhookToDelete(webhookId);
        setWebhookResendModalOpen(true);
    };

    // Função para abrir o modal de detalhes
    const openDetailsModal = (deliveryId: string) => {
        setDeliveryId(deliveryId);
        setWebhookToDelete(deliveryId); // Define também o webhookToDelete para permitir o reenvio
        setWebhookDetailsModalOpen(true);
    };

    // Função para reenviar webhook
    const handleResendWebhook = async () => {
        if (!webhookToDelete) return;

        setWebhookResendLoading(true);
        try {
            // Buscar o evento selecionado para obter o payload e o endpoint
            const selectedDelivery = webhookDeliveries.find(delivery => delivery.id === webhookToDelete);

            if (!selectedDelivery) {
                throw new Error("Evento não encontrado");
            }

            // Enviar a solicitação de reenvio para o backend
            // O backend será responsável por reenviar o webhook com o payload original
            await api.post(
                `/api/company-webhook/${webhookIdToUse}/events/${selectedDelivery.id}/resend`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            // Fechar o modal e limpar o estado
            setWebhookResendModalOpen(false);
            setWebhookToDelete(null);
            setWebhookResendLoading(false);

            // Mostrar mensagem de sucesso
            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Evento reenviado com sucesso."
            });

            // Esconder a mensagem após 3 segundos
            setTimeout(() => setAlertInfo(null), 3000);

            // Recarregar os eventos após o reenvio
            fetchWebhookEvents();
        } catch (error) {
            console.error("Erro ao reenviar webhook:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro ao reenviar evento!",
                message: "Verifique se o endpoint existe e tente novamente."
            });
            setTimeout(() => setAlertInfo(null), 5000);
            setWebhookResendModalOpen(false);
            setWebhookResendLoading(false);
        }
    };

    // Mapeamento para eventos de webhook
    const eventMap: Record<string, { label: string; color: BadgeColor }> = {
        sale_approved: { label: 'Venda Aprovada', color: 'success' },
        sale_rejected: { label: 'Venda Recusada', color: 'error' },
        sale_refunded: { label: 'Venda Reembolsada', color: 'warning' },
        sale_chargeback: { label: 'Chargeback', color: 'error' },
        sale_abandoned: { label: 'Abandono', color: 'light' },
        sale_pix_generated: { label: 'PIX Gerado', color: 'info' },
        sale_boleto_generated: { label: 'Boleto Gerado', color: 'info' },
        subscription_canceled: { label: 'Assinatura Cancelada', color: 'warning' },
        subscription_overdue: { label: 'Assinatura Atrasada', color: 'error' },
        subscription_renewed: { label: 'Assinatura Renovada', color: 'success' }
    };

    // Verificar se não há eventos
    if (webhookIdToUse && !loading && webhookDeliveries.length === 0) {
        return (
            <ComponentCard title="Todos os eventos">
                <div className="col-span-12 space-y-6">
                    <div className="mb-6">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            {/* Campo de busca - com largura limitada */}
                            <div className="relative w-full md:w-1/3 lg:w-1/4">
                                <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                    <svg
                                        className="fill-gray-500 dark:fill-gray-400"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                            fill=""
                                        />
                                    </svg>
                                </button>
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1); // Resetar para página 1 ao buscar
                                    }}
                                />
                            </div>

                            {/* Select */}
                            <div className="flex gap-4">
                                <Select
                                    options={options}
                                    placeholder="Todos os eventos"
                                    onChange={(value) => {
                                        setStatusFilter(value);
                                        setCurrentPage(1); // Resetar para página 1 ao mudar filtros
                                    }}
                                    className="dark:bg-dark-900 h-11"
                                />
                                <DateRangePicker
                                    onChange={handleDateRangeChange}
                                    initialStartDate={dateRange.startDate}
                                    initialEndDate={dateRange.endDate}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center py-8">
                    <div className="p-8 text-center dark:text-white/90">
                        Nenhum evento encontrado.
                    </div>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Todos os eventos">
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Campo de busca - com largura limitada */}
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Buscar..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1); // Resetar para página 1 ao buscar
                                }}
                            />
                        </div>

                        {/* Select */}
                        <div className="flex gap-4">
                            <Select
                                options={options}
                                placeholder="Todos os eventos"
                                onChange={(value) => {
                                    setStatusFilter(value);
                                    setCurrentPage(1); // Resetar para página 1 ao mudar filtros
                                }}
                                className="dark:bg-dark-900 h-11 w-full md:w-72"
                            />
                            <DateRangePicker
                                onChange={handleDateRangeChange}
                                initialStartDate={dateRange.startDate}
                                initialEndDate={dateRange.endDate}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {alertInfo && alertInfo.show && (
                <div className="mb-4">
                    <Alert
                        variant={alertInfo.type as "success" | "error" | "warning" | "info"}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}

            {loading ? (
                <div className="py-24 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl">
                    <div className="max-w-full overflow-x-auto">
                        <Table>
                            <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                <TableRow>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Data
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Endpoint
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Evento
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                    >
                                        Status
                                    </TableCell>
                                    <TableCell
                                        isHeader
                                        className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                                    >
                                        Ações
                                    </TableCell>
                                </TableRow>
                            </TableHeader>

                            {/* Table Body */}
                            <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                {webhookDeliveries.map((delivery) => (
                                    <TableRow key={delivery.id}>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            {formatDate(delivery.createdAt)}
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400 max-w-[480px] truncate">
                                            {delivery.endpoint}
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            <div className="flex flex-wrap gap-1.5">
                                                {delivery.event && eventMap[delivery.event] ? (
                                                    <Badge
                                                        size="sm"
                                                        color={eventMap[delivery.event].color}
                                                    >
                                                        {eventMap[delivery.event].label}
                                                    </Badge>
                                                ) : (
                                                    <Badge size="sm" color="light">
                                                        {delivery.event || "Desconhecido"}
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                            <Badge
                                                size="sm"
                                                color={delivery.success ? "success" : "error"}
                                            >
                                                {delivery.success ? "Entregue" : `Falha`}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                            <div className="flex justify-end gap-2 relative">
                                                <MoreButton
                                                    options={[
                                                        {
                                                            label: "Reenviar",
                                                            onClick: () => openWebhookModal(delivery.id),
                                                            icon: <Webhook className="size-4" />
                                                        },
                                                        {
                                                            label: "Detalhes",
                                                            onClick: () => openDetailsModal(delivery.id),
                                                            icon: <Eye className="size-4" />
                                                        }
                                                    ]}
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {totalPages > 1 && (
                            <div className="flex justify-center mt-6">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal de reenvio de Webhook */}
            <Modal
                isOpen={webhookResendModalOpen}
                onClose={() => setWebhookResendModalOpen(false)}
                size="sm"
                onConfirm={handleResendWebhook}
                confirmText="Reenviar"
                cancelText="Cancelar"
                confirmButtonClass="bg-blue-500 hover:bg-blue-600 text-white"
                confirmLoading={webhookResendLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <Webhook className="h-16 w-16 text-blue-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Reenviar Webhook?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        O webhook será reenviado para o endpoint original.
                    </p>
                </div>
            </Modal>

            {/* Modal de detalhes de Webhook */}
            <Modal
                isOpen={webhookDetailsModalOpen}
                onClose={() => setWebhookDetailsModalOpen(false)}
                size="lg"
                title="Detalhes do Webhook"
                onConfirm={handleResendWebhook}
                confirmText="Reenviar"
                cancelText="Fechar"
                confirmButtonClass="bg-blue-500 hover:bg-blue-600 text-white"
                confirmLoading={webhookResendLoading}
            >
                {deliveryId && (
                    <div className="py-4">
                        {/* Informações do webhook */}
                        {webhookDeliveries.filter(delivery => delivery.id === deliveryId).map(delivery => (
                            <div key={delivery.id} className="space-y-6">
                                {/* Cabeçalho com informações principais */}
                                <div className="space-y-3">
                                    <p className="text-base font-medium text-gray-900 dark:text-white break-all">Evento:</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {delivery.event && eventMap[delivery.event] ? (
                                            <Badge
                                                size="sm"
                                                color={eventMap[delivery.event].color}
                                            >
                                                {eventMap[delivery.event].label}
                                            </Badge>
                                        ) : (
                                            <Badge size="sm" color="light">
                                                {delivery.event || "Desconhecido"}
                                            </Badge>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-base font-medium text-gray-900 dark:text-white break-all">Endpoint:</p>
                                        <p className="block text-gray-500 text-theme-sm dark:text-gray-400">{delivery.endpoint || "N/A"}</p>
                                    </div>
                                    <div>
                                        <p className="text-base font-medium text-gray-900 dark:text-white break-all">Data do envio:</p>
                                        <p className="block text-gray-500 text-theme-sm dark:text-gray-400">
                                            {delivery.createdAt ? new Date(delivery.createdAt).toLocaleString() : "N/A"}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-base font-medium text-gray-900 dark:text-white break-all">Tentativas:</p>
                                        <p className="block text-gray-500 text-theme-sm dark:text-gray-400">{delivery.attemptCount || 1}</p>
                                    </div>

                                    <div>
                                        <p className="text-base font-medium text-gray-900 dark:text-white break-all">Status da última resposta:</p>
                                        <div className="flex items-center">
                                            {delivery.success ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                    Sucesso
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                                    Falha
                                                </span>
                                            )}
                                            {delivery.responseStatus && (
                                                <span className="ml-2 block text-gray-500 text-theme-sm dark:text-gray-400">
                                                    ({delivery.responseStatus})
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Linha divisória */}
                                <div className="border-b border-gray-100 dark:border-gray-700 my-6"></div>

                                {/* Requisição e Resposta */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {(() => {
                                        const requestText = JSON.stringify(delivery.payload, null, 2);
                                        const responseText = delivery.responseBody || "";
                                        const requestKey = `${delivery.id}-request`;
                                        const responseKey = `${delivery.id}-response`;
                                        return (
                                            <>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className="text-gray-700 dark:text-gray-300 font-medium mb-1">Requisição</div>
                                                            <FileText className="h-4 w-4 ml-2 text-gray-500" />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopyContent(requestKey, requestText)}
                                                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                                            title="Copiar requisição"
                                                        >
                                                            {copiedKey === requestKey ? (
                                                                <>
                                                                    <Check className="h-4 w-4 text-green-500" />
                                                                    <span className="text-green-500">Copiado</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy className="h-4 w-4" />
                                                                    <span>Copiar</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                    <div className="bg-gray-100 dark:bg-gray-800 rounded-md p-3 overflow-auto max-h-80">
                                                        <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                            {requestText}
                                                        </pre>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className="text-gray-700 dark:text-gray-300 font-medium mb-1">Resposta</div>
                                                            <FileText className="h-4 w-4 ml-2 text-gray-500" />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleCopyContent(responseKey, responseText)}
                                                            disabled={!responseText}
                                                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                            title={responseText ? "Copiar resposta" : "Sem resposta para copiar"}
                                                        >
                                                            {copiedKey === responseKey ? (
                                                                <>
                                                                    <Check className="h-4 w-4 text-green-500" />
                                                                    <span className="text-green-500">Copiado</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Copy className="h-4 w-4" />
                                                                    <span>Copiar</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                    <div className="bg-gray-100 dark:bg-gray-800 rounded-md p-3 overflow-auto max-h-80">
                                                        <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                            {responseText || "Sem resposta"}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal>
        </ComponentCard>
    );
}